import {
  Box,
  ButtonGroup,
  type Column,
  type ColumnDynamicsSpec,
  Dialog,
  FormControlSizes,
  FormGroup,
  getColumnDynamicsState,
  Input,
  type SecurityMarketDataColumnParams,
  Stack,
  ToggleButton,
  useCallbackRef,
  useDisclosureWithContext,
} from '@talos/kyoko';
import { useMemo, useState } from 'react';
import { MarketSelector } from '../../../components/MarketSelector/MarketSelector';
import { useMarketSelectorAggregationPresets } from '../../../components/MarketSelector/presets/useMarketSelectorAggregationPresets';
import type { MarketSelectorValue } from '../../../components/MarketSelector/types';
import { useMarketSelector } from '../../../components/MarketSelector/useMarketSelector';
import { EMPTY_MARKET_SELECTOR_VALUE } from '../../../components/MarketSelector/utils/OrderMarketsField';
import type { PriceBlotterEntity } from './PriceBlotterEntity';
import { usePriceGridMarketSelectorItems } from './usePriceGridMarketSelectorItems';

function getMarketsFromSelectorValue(marketSelectorValue?: MarketSelectorValue): string[] | undefined {
  const selections = marketSelectorValue?.selections;
  return selections != null && selections.length > 0 ? selections : undefined;
}

export function getPriceColumn(
  dynamics: NonNullable<Column<SecurityMarketDataColumnParams, PriceBlotterEntity, PriceColumnSettings>['dynamics']>
) {
  const settings = getColumnDynamicsState(dynamics);
  return {
    type: 'securityMarketData',
    field: 'security.Symbol',
    title: settings?.title ?? 'Price',
    id: dynamics.colID,
    initialWidth: 400,
    params: {
      marketDataCollectionPath: 'marketData' satisfies keyof PriceBlotterEntity,
      Markets: getMarketsFromSelectorValue(settings?.marketSelectorValue),
    },
    dynamics,
  } as const;
}

export function getLegPriceColumn(
  dynamics: NonNullable<Column<SecurityMarketDataColumnParams, PriceBlotterEntity, LegPriceColumnSettings>['dynamics']>
) {
  const settings = getColumnDynamicsState(dynamics);
  const field = settings?.legIndex == null ? undefined : `security.MultilegDetails.Legs[${settings.legIndex}].Symbol`;

  return {
    type: 'securityMarketData',
    field,
    title: settings?.title ?? 'Leg Price',
    id: dynamics.colID,
    initialWidth: 400,
    params: {
      marketDataCollectionPath: 'marketData' satisfies keyof PriceBlotterEntity,
      Markets: getMarketsFromSelectorValue(settings?.marketSelectorValue),
    },
    dynamics,
  } as const;
}

export type PriceBlotterColumnDynamicsSpecType = 'price' | 'leg-price';

export const usePriceBlotterColumnDynamicsSpecs = () => {
  const priceColumnSettingsDialog = useDisclosureWithContext<PriceColumnSettingsDialogContext>();
  const legPriceColumnSettingsDialog = useDisclosureWithContext<LegPriceColumnSettingsDialogContext>();

  const openPriceColumnSettings = useCallbackRef(
    (currentSettings: PriceColumnSettings | undefined, save: (newSettings: PriceColumnSettings | undefined) => void) =>
      priceColumnSettingsDialog.open({ currentSettings, save })
  );

  const openLegPriceColumnSettings = useCallbackRef(
    (
      currentSettings: LegPriceColumnSettings | undefined,
      save: (newSettings: LegPriceColumnSettings | undefined) => void
    ) => legPriceColumnSettingsDialog.open({ currentSettings, save })
  );

  const columnDynamicsSpecs = useMemo(
    () => [
      {
        label: 'Price Column',
        type: 'price' satisfies PriceBlotterColumnDynamicsSpecType,
        getColumn: getPriceColumn,
        openSettingsOnCreation: true,
        openSettings: openPriceColumnSettings,
        getInitialSettings: () => ({}),
      } satisfies ColumnDynamicsSpec<SecurityMarketDataColumnParams, PriceBlotterEntity, PriceColumnSettings>,
      {
        label: 'Leg Price Column',
        type: 'leg-price' satisfies PriceBlotterColumnDynamicsSpecType,
        getColumn: getLegPriceColumn,
        openSettingsOnCreation: true,
        openSettings: openLegPriceColumnSettings,
        getInitialSettings: () => ({}),
      },
    ],
    [openPriceColumnSettings, openLegPriceColumnSettings]
  );

  const dialogs = (
    <>
      <PriceColumnSettingsDialog {...priceColumnSettingsDialog} />,
      <LegPriceColumnSettingsDialog {...legPriceColumnSettingsDialog} />,
    </>
  );

  return {
    columnDynamicsSpecs,
    dialogs,
  };
};

export interface LegPriceColumnSettings {
  title?: string;
  legIndex?: number;
  marketSelectorValue?: MarketSelectorValue;
}

export interface PriceColumnSettings {
  title?: string;
  marketSelectorValue?: MarketSelectorValue;
}

interface PriceColumnSettingsDialogContext {
  currentSettings: PriceColumnSettings | undefined;
  save: (newSettings: PriceColumnSettings) => void;
}

interface LegPriceColumnSettingsDialogContext {
  currentSettings: LegPriceColumnSettings | undefined;
  save: (newSettings: LegPriceColumnSettings) => void;
}

type PriceColumnSettingsDialogProps = ReturnType<typeof useDisclosureWithContext<PriceColumnSettingsDialogContext>>;
type LegPriceColumnSettingsDialogProps = ReturnType<
  typeof useDisclosureWithContext<LegPriceColumnSettingsDialogContext>
>;

const PriceColumnSettingsDialog = ({ context, ...dialogProps }: PriceColumnSettingsDialogProps) => {
  if (!context) {
    return null;
  }

  return <PriceColumnSettingsDialogContent key={dialogProps.openId} context={context} {...dialogProps} />;
};

const PriceColumnSettingsDialogContent = ({
  context,
  ...dialogProps
}: Omit<PriceColumnSettingsDialogProps, 'context'> & { context: PriceColumnSettingsDialogContext }) => {
  const [title, setTitle] = useState(context.currentSettings?.title ?? '');
  const [marketSelectorValue, setMarketSelectorValue] = useState(context.currentSettings?.marketSelectorValue);

  const handleConfirm = useCallbackRef(() => {
    context.save({
      title,
      marketSelectorValue,
    });
  });

  const { aggregationPresets } = useMarketSelectorAggregationPresets();
  const marketSelector = useMarketSelector({
    value: marketSelectorValue ?? EMPTY_MARKET_SELECTOR_VALUE,
    items: usePriceGridMarketSelectorItems(),
    onChange: setMarketSelectorValue,
    presets: aggregationPresets,
  });

  return (
    <Dialog
      {...dialogProps}
      title="Price Column Settings"
      confirmLabel="Save"
      onConfirm={handleConfirm}
      width={360}
      alignContent="left"
      dataTestId="price-column-settings-dialog"
    >
      <Stack gap="spacing12">
        <FormGroup label="Title" mb="0">
          <Input autoFocus placeholder="Price" value={title} onChange={e => setTitle(e.target.value)} />
        </FormGroup>

        <FormGroup label="Markets" tooltip="An empty selection defaults to all possible Markets">
          <Box h="200px">
            <MarketSelector marketSelectorAPI={marketSelector} />
          </Box>
        </FormGroup>
      </Stack>
    </Dialog>
  );
};

const LegPriceColumnSettingsDialog = ({ context, ...dialogProps }: LegPriceColumnSettingsDialogProps) => {
  if (!context) {
    return null;
  }

  return <LegPriceColumnSettingsDialogContent key={dialogProps.openId} context={context} {...dialogProps} />;
};

const LegPriceColumnSettingsDialogContent = ({
  context,
  ...dialogProps
}: Omit<LegPriceColumnSettingsDialogProps, 'context'> & { context: LegPriceColumnSettingsDialogContext }) => {
  const [title, setTitle] = useState(context.currentSettings?.title ?? '');
  const [legIndex, setLegIndex] = useState<number | undefined>(context.currentSettings?.legIndex);
  const [marketSelectorValue, setMarketSelectorValue] = useState(context.currentSettings?.marketSelectorValue);

  const handleConfirm = useCallbackRef(() => {
    context.save({
      title,
      legIndex,
      marketSelectorValue,
    });
  });

  const { aggregationPresets } = useMarketSelectorAggregationPresets();
  const marketSelector = useMarketSelector({
    value: marketSelectorValue ?? EMPTY_MARKET_SELECTOR_VALUE,
    items: usePriceGridMarketSelectorItems(),
    onChange: setMarketSelectorValue,
    presets: aggregationPresets,
  });

  return (
    <Dialog
      {...dialogProps}
      title="Leg Price Column Settings"
      confirmLabel="Save"
      onConfirm={handleConfirm}
      width={360}
      alignContent="left"
      dataTestId="leg-price-column-settings-dialog"
    >
      <Stack gap="spacing12">
        <FormGroup label="Title" mb="0">
          <Input autoFocus placeholder="Price" value={title} onChange={e => setTitle(e.target.value)} />
        </FormGroup>
        <FormGroup label="Leg Index" mb="0">
          <ButtonGroup size={FormControlSizes.Small}>
            <ToggleButton selected={legIndex === 0} onClick={() => setLegIndex(0)}>
              Leg 1
            </ToggleButton>
            <ToggleButton selected={legIndex === 1} onClick={() => setLegIndex(1)}>
              Leg 2
            </ToggleButton>
          </ButtonGroup>
        </FormGroup>

        <FormGroup label="Markets" tooltip="An empty selection defaults to all possible Markets">
          <Box h="200px">
            <MarketSelector marketSelectorAPI={marketSelector} />
          </Box>
        </FormGroup>
      </Stack>
    </Dialog>
  );
};
